import { Button } from '@/components/ui/button';
import posthog from 'posthog-js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { getStripeUrl } from '@/lib/stripe-links';
import {
  ArrowRight,
  Globe,
  Mail,
  Phone,
  User,
  Users,
} from 'lucide-react';
import { HiSparkles, HiBolt, HiCheckCircle, HiCpuChip } from 'react-icons/hi2';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export const SaaSShowcase = () => {
  const [user, setUser] = useState<{ id: string; email?: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });
  }, []);

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    employees: '',
    message: '',
  });

  const handleTierButtonClick = async (tier: typeof tiers[number]) => {
    posthog.capture('pricing_plan_selected', { plan: tier.name.toLowerCase() });
    if (tier.name === 'Enterprise') {
      setContactModalOpen(true);
      return;
    }

    if (!user) {
      navigate('/auth?mode=signup');
      return;
    }

    if (!tier.stripeUrl) {
      toast.error('Configuration missing', {
        description: 'This payment link has not been configured yet.'
      });
      return;
    }

    // Payment Links: append client_reference_id so webhook can link payment → user
    try {
      const checkoutUrl = new URL(tier.stripeUrl);
      checkoutUrl.searchParams.set('client_reference_id', user.id);
      window.location.href = checkoutUrl.toString();
    } catch (e) {
      console.error('Invalid Stripe URL:', tier.stripeUrl);
      toast.error('Invalid checkout link');
    }
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setContactModalOpen(false);
    setContactForm({
      name: '',
      email: '',
      company: '',
      phone: '',
      employees: '',
      message: '',
    });
    window.location.href = '/contact';
  };

  const tiers = [
    {
      name: 'Starter',
      price: '$9.99',
      period: 'mo',
      icon: <HiSparkles className="w-5 h-5 text-primary" />,
      popular: false,
      description: 'Ideal for small projects',
      features: [
        '1 AI chatbot',
        'Unlimited chats',
        'Contextualized answers',
        '5 knowledge docs',
        'Email support',
      ],
      badge: '',
      color: 'border-border bg-card',
      ctaText: 'Start Free Trial',
      stripeUrl: getStripeUrl('starter')
    },
    {
      name: 'Business',
      price: '$19.99',
      period: 'mo',
      icon: <HiBolt className="w-5 h-5 text-primary" />,
      popular: true,
      description: 'For growing scale',
      features: [
        '10 AI chatbots',
        'Unlimited chats',
        'Contextualized answers',
        '50 knowledge docs',
        'Website extraction (50/mo)',
        'Custom branding',
        'Priority support',
      ],
      badge: 'Most Popular',
      color: 'border-primary/20',
      ctaText: 'Start Business Plan',
      stripeUrl: getStripeUrl('business')
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'pricing',
      icon: <HiCpuChip className="w-5 h-5 text-primary" />,
      popular: false,
      description: 'Bespoke solutions',
      features: [
        'Unlimited chatbots',
        'Unlimited chats',
        'Contextualized answers',
        'Bulk conversations',
        'White-label options',
        'Dedicated support',
      ],
      badge: '',
      color: 'border-border',
      ctaText: 'Contact Sales',
    },
  ];

  return (
    <section id="pricing" className="py-20 md:py-28">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl font-bold text-foreground tracking-tight">
            Plans for every stage
          </h2>
        </div>

        {/* Pricing Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {tiers.map((tier) => (
            <div key={tier.name}>
              <Card
                className={`flex flex-col h-full overflow-hidden transition-all duration-300 ${tier.popular
                  ? 'border-primary/50 shadow-md hover:shadow-xl hover:shadow-primary/15 hover:-translate-y-1'
                  : 'bg-card border-border hover:border-primary/20 hover:shadow-md hover:-translate-y-0.5'
                  }`}
              >
                {tier.popular && (
                  <div className="bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest text-center py-1">
                    Most Popular
                  </div>
                )}

                <CardHeader className="text-center pb-4 pt-8">
                  <div className="flex justify-center mb-4">
                    {tier.icon}
                  </div>
                  <CardTitle className="font-display text-xl font-bold text-foreground mb-1">
                    {tier.name}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mb-3">{tier.description}</p>
                  <div className="font-display text-4xl font-bold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {tier.price}
                    {tier.price !== 'Custom' && (
                      <span className="text-sm font-normal text-muted-foreground ml-1">
                        /{tier.period}
                      </span>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="flex flex-col flex-1 space-y-6 px-6 pb-6">
                  <div className="flex-1">
                    <ul className="space-y-3">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <HiCheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    onClick={() => handleTierButtonClick(tier)}
                    className={`w-full transition-all ${!tier.popular
                      ? 'bg-secondary hover:bg-secondary/80 text-secondary-foreground'
                      : ''
                      }`}
                    variant={tier.popular ? 'cta' : 'secondary'}
                  >
                    {tier.ctaText}
                  </Button>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Enterprise Contact Modal */}
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl font-bold">
              Enterprise Solutions
            </DialogTitle>
            <p className="text-center text-muted-foreground">
              Tell us about your needs and we'll create a custom solution
            </p>
          </DialogHeader>

          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    required
                    placeholder="Your full name"
                    value={contactForm.name}
                    onChange={e =>
                      setContactForm({ ...contactForm, name: e.target.value })
                    }
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder="your@company.com"
                    value={contactForm.email}
                    onChange={e =>
                      setContactForm({ ...contactForm, email: e.target.value })
                    }
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company">Company *</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="company"
                    required
                    placeholder="Company name"
                    value={contactForm.company}
                    onChange={e =>
                      setContactForm({
                        ...contactForm,
                        company: e.target.value,
                      })
                    }
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    placeholder="+1 (555) 000-0000"
                    value={contactForm.phone}
                    onChange={e =>
                      setContactForm({ ...contactForm, phone: e.target.value })
                    }
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employees">Company Size</Label>
              <div className="relative">
                <Users className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="employees"
                  placeholder="Number of employees"
                  value={contactForm.employees}
                  onChange={e =>
                    setContactForm({
                      ...contactForm,
                      employees: e.target.value,
                    })
                  }
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Requirements</Label>
              <Textarea
                id="message"
                placeholder="Tell us about your customer support needs, expected volume, and any specific requirements..."
                value={contactForm.message}
                onChange={e =>
                  setContactForm({ ...contactForm, message: e.target.value })
                }
                className="min-h-[100px]"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              variant="cta"
            >
              Get Custom Quote
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
